import * as React from "react";
import classNames from "classnames";
import { Icon, Intent, Menu, MenuItem, PopoverInteractionKind, Tag } from "@blueprintjs/core";
import Router from "next/router";
import AnnotationStore from "../../stores/annotation-store";
import copyToClipboard from "copy-to-clipboard";
import { AppToaster } from "../toaster";
import { logEvent } from "../../utils/analytics";
import { Annotation, CollectionName } from "../../api";
import { AMR_SOURCES } from "../../utils/constants";
import { useContext } from "react";
import { StoresContext } from "../../pages/_app";
import { Popover2 } from "@blueprintjs/popover2";
import qs from "qs";

interface AnnotationTableRowProps {
  collectionName: CollectionName | null;
  selected: boolean;
  isComparison: boolean;
  annotation: Annotation;
  annotationStore?: AnnotationStore;
  selectAnnotation: (annotation: Annotation) => void;
}

const AnnotationTableRow = (props: AnnotationTableRowProps) => {
  const rootStore = useContext(StoresContext);
  const annotationStore = rootStore.annotationStore;
  const annotation = props.annotation;

  const gene = annotation.gene;
  const genomeName =
    annotationStore!.annotations.metadata[annotation.annotation_set.uuid]?.genome.name;
  const geneName = gene ? gene.name : "";
  const rawGeneECNumber = gene?.ec_number || "";
  const geneECNumbers = rawGeneECNumber
    .split(":")
    .map((x) => x.trim())
    .filter((x) => x !== "");
  const geneProduct = gene ? gene.product : "hypothetical protein";
  const geneType = gene ? gene.gene_type : "CDS";
  const geneRefseqId = gene ? gene.refseq_id : "";
  const geneOrthoDBId = gene ? gene.uniprot_id : "";
  let classes;
  let geneNameCell;
  let geneECNumberCell;
  let externalIDCell;
  let tagsCell;

  const isFungal =
    !!props.collectionName && ["mycology", "protistology"].includes(props.collectionName);

  const renderAmrTag = () => {
    if (annotation.amr_sources.length == 0) {
      return;
    }
    const sources = (
      <>
        <span className="amr-tag-dash">&ndash;</span>
        {[...annotation.amr_sources]
          .sort((a, b) => AMR_SOURCES.indexOf(a) - AMR_SOURCES.indexOf(b))
          .join(", ")}
      </>
    );
    const allSources = (AMR_SOURCES as string[]).reduce(
      (acc, curr, idx, array) => acc + (idx < array.length - 1 ? ", " : ", and ") + curr
    );

    return (
      <Popover2
        interactionKind={PopoverInteractionKind.HOVER}
        placement="top"
        content={`AMR Genes are sourced from ${allSources}`}
        popoverClassName="info-tooltip"
      >
        <Tag interactive={true} className="genome-tag">
          AMR{sources}
        </Tag>
      </Popover2>
    );
  };
  if (isFungal) {
    classes = classNames("fungal-annotation-row");
    geneNameCell = <td />;
    externalIDCell = (
      <td>
        <a
          href={`https://v100.orthodb.org/?query=${geneOrthoDBId}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          {geneOrthoDBId}
        </a>
      </td>
    );
  } else {
    classes = classNames("annotation-row", {
      "annotation-row--selected": props.selected,
      "annotation-row--comparison": props.isComparison,
    });
    geneNameCell = (
      <td>
        <i>{geneName}</i>
      </td>
    );
    externalIDCell = (
      <td>
        <a
          href={"https://www.ncbi.nlm.nih.gov/protein/" + geneRefseqId}
          rel="noopener noreferrer"
          target="_blank"
        >
          {geneRefseqId}
        </a>
      </td>
    );
    tagsCell = (
      <td className={"tag-cell-flex"}>
        <div className="tag-cell-flex__inner">{renderAmrTag()}</div>
      </td>
    );
    geneECNumberCell = (
      <td>
        <div className="annotation-row__ec-numbers">
          {geneECNumbers.map((num) => (
            <a
              key={num}
              href={"https://enzyme.expasy.org/EC/" + num}
              rel="noopener noreferrer"
              target="_blank"
            >
              {num}
            </a>
          ))}
        </div>
      </td>
    );
  }

  const searchSequence = async () => {
    const ntSequence = await annotationStore.getNTSequence(annotation.uuid);
    if (ntSequence) {
      rootStore.genomeStore.resetSearchFilters();
      const queryString = qs.stringify({
        sequence: ntSequence,
        orderBy: "search_similarity",
        ascending: false,
      });
      await Router.push({ pathname: "/genomes", query: queryString });
    }
    return null;
  };

  const copyNTSeq = async () => {
    const ntSequence = await annotationStore.getNTSequence(annotation.uuid);
    if (ntSequence) {
      // eslint-disable-next-line
      AppToaster!.show({
        message: "Nucleotide sequence copied",
        intent: Intent.PRIMARY,
      });
      logEvent("annotation", "Copy NT sequence", annotation.uuid);
      copyToClipboard(ntSequence);
    }
    return null;
  };

  const copyAASeq = async () => {
    const aaSequence = await annotationStore!.getAASequence(annotation.uuid);

    if (aaSequence) {
      // eslint-disable-next-line
      AppToaster!.show({
        message: "Amino acid sequence copied",
        intent: Intent.PRIMARY,
      });
      logEvent("annotation", "Copy AA sequence", annotation.uuid);
      copyToClipboard(aaSequence);
    }
    return null;
  };

  const onClickSearchSequence = () => {
    rootStore.userStore.tryToDoGuardedAction(() => {
      return searchSequence();
    }, false);
  };

  const onClickCopyNTSeq = () => {
    rootStore.userStore.tryToDoGuardedAction(() => {
      return copyNTSeq();
    }, false);
  };

  const onClickCopyAASeq = () => {
    rootStore.userStore.tryToDoGuardedAction(() => {
      return copyAASeq();
    }, false);
  };

  // NOTE: We add "+ 1" to the absoluteStartLoc to convert from zero-indexed
  //       to one-indexed for our scientific users. We do NOT do the same for
  //       absoluteEndLoc because that number is exclusive
  const { absolute_start_loc, absolute_end_loc, is_forward_strand, contig_ix } = annotation;
  let startLoc = (absolute_start_loc || 0) + 1;
  let endLoc = absolute_end_loc || 0;
  if (!is_forward_strand) {
    const temp = startLoc;
    startLoc = endLoc;
    endLoc = temp;
  }
  return (
    <tr className={classes}>
      {props.isComparison && <td>{genomeName}</td>}
      <td>{contig_ix + 1}</td>
      <td>{startLoc}</td>
      <td>{endLoc}</td>
      {geneNameCell}
      <td>
        <div className="gene-product-cell">{geneProduct}</div>
      </td>
      {tagsCell}
      {geneECNumberCell}
      <td>{geneType}</td>
      {externalIDCell}
      {!isFungal && (
        <td>
          <Icon
            className="link-icon"
            icon="geolocation"
            onClick={() => props.selectAnnotation(annotation)}
          />
        </td>
      )}
      {
        rootStore.userStore.hasActiveMicrobeSubscription() && (
          <td>
            <Popover2
              position="auto"
              canEscapeKeyClose={true}
              content={
                <Menu>
                  <MenuItem text="Copy nucleotide sequence" onClick={onClickCopyNTSeq} />
                  {geneType === "CDS" && (
                    <MenuItem text="Copy amino acid sequence" onClick={onClickCopyAASeq} />
                  )}
                  <MenuItem onClick={onClickSearchSequence} text="Search for nucleotide sequence" />
                </Menu>
              }
            >
              <Icon className="link-icon" icon="more" />
            </Popover2>
          </td>
        ) /* sequence copy and search only available to subscribers with microbe access */
      }
    </tr>
  );
};

export default AnnotationTableRow;
